import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import { FiFilter, FiX } from 'react-icons/fi';
import { Crumbs, GlassChip, AuroraBlobs, GrainOverlay, Reveal, stagger, fadeUp } from '../components/ui';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const FABRICS = ['Cotton', 'Mul Mul', 'Linen', 'Rayon', 'Silk', 'Chanderi', 'Velvet'];
const COLORS = [
  { name: 'Claret', hex: '#7A1F2B' }, { name: 'Indigo', hex: '#1E3A5F' },
  { name: 'Olive', hex: '#5C6A3A' }, { name: 'Gold', hex: '#B08A3E' },
  { name: 'Coffee', hex: '#3D2C20' }, { name: 'Pearl', hex: '#EFE6D3' },
];
const SORT_OPTIONS = ['Featured', 'Newest', 'Price: Low to High', 'Price: High to Low', 'Rating'];

export default function CategoryPage() {
  const { slug } = useParams();
  const [filterOpen, setFilterOpen] = useState(false);
  const [sort, setSort] = useState('Featured');
  const [page, setPage] = useState(1);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedFabrics, setSelectedFabrics] = useState([]);

  const isSpecial = slug === 'new' || slug === 'all';

  const { data: catData } = useQuery({
    queryKey: ['category', slug],
    queryFn: () => api.get(`/categories/${slug}`),
    enabled: !!slug && !isSpecial,
  });

  const { data: catsData } = useQuery({ queryKey: ['categories'], queryFn: () => api.get('/categories') });

  const sortMap = {
    'Featured': '', 'Newest': 'Newest',
    'Price: Low to High': 'Price ↑', 'Price: High to Low': 'Price ↓', 'Rating': 'Rating',
  };

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products', slug, sort, page, maxPrice, selectedSizes],
    queryFn: () => {
      const params = new URLSearchParams();
      if (slug && !isSpecial) params.set('category', catData?.category?._id || '');
      if (slug === 'new') params.set('badge', 'New');
      params.set('sort', sortMap[sort]);
      params.set('maxPrice', maxPrice);
      params.set('page', page);
      params.set('limit', 12);
      return api.get(`/products?${params}`);
    },
    enabled: isSpecial || !!catData?.category,
  });

  const category = catData?.category;
  const products = productsData?.products || [];
  const total = productsData?.total || 0;
  const pages = productsData?.pages || 1;
  const categories = catsData?.categories || [];

  const toggleArr = (val, arr, set) => set(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);

  return (
    <div>
      {/* ─── Aurora hero ─── */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '56px 24px 48px' }}>
        <AuroraBlobs />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto' }}>
          {(() => {
            const pageTitle = slug === 'all' ? 'All Kurtis' : slug === 'new' ? 'New Arrivals' : category?.name;
            const pageHindi = slug === 'all' ? 'सब कुछ' : slug === 'new' ? 'नई आवक' : category?.hindi;
            return (
              <>
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                  <Crumbs items={[{ label: 'Home', to: '/' }, { label: pageTitle || '…' }]} />
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
                  <div>
                    {pageHindi && <div className="hindi" style={{ fontSize: 18, color: 'var(--gold)', marginBottom: 6, opacity: 0.9 }}>{pageHindi}</div>}
                    <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', color: '#FFF', lineHeight: 1, letterSpacing: '-0.02em', marginBottom: 12 }}>
                      {pageTitle}
                    </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: 'rgba(237,227,209,0.6)' }}>
                <span>{total} pieces</span>
                {category?.tag && (
                  <>
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(237,227,209,0.4)' }} />
                    <span style={{ color: 'var(--gold)', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, fontSize: 11 }}>{category.tag}</span>
                  </>
                )}
              </div>
            </div>
            <button onClick={() => setFilterOpen(v => !v)} className="cp-filter-toggle" style={{
              display: 'none', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 999,
              background: 'rgba(255,255,255,0.06)', color: '#EDE3D1', border: '1px solid rgba(237,227,209,0.18)',
              backdropFilter: 'blur(12px)', fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
            }}>
              <FiFilter size={14} /> Filters
            </button>
          </motion.div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Sub-cat chips */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 24px 0' }}>
        <div className="scrollx" style={{ display: 'flex', gap: 8, paddingBottom: 6 }}>
          <Link to="/category/all" className={`chip ${slug === 'all' ? 'chip-active' : ''}`} style={{ textDecoration: 'none', flexShrink: 0 }}>All</Link>
          <Link to="/category/new" className={`chip ${slug === 'new' ? 'chip-active' : ''}`} style={{ textDecoration: 'none', flexShrink: 0 }}>New Arrivals</Link>
          {categories.map(c => (
            <Link key={c._id} to={`/category/${c.slug}`} className={`chip ${c.slug === slug ? 'chip-active' : ''}`} style={{ textDecoration: 'none', flexShrink: 0 }}>
              {c.name}
            </Link>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 56px', display: 'flex', gap: 40 }} className="cp-layout">
        {/* ─── Sidebar filters ─── */}
        <aside className="cp-sidebar" style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 32, position: 'sticky', top: 96, alignSelf: 'flex-start' }}>
          <FilterGroup label="Sort by">
            {SORT_OPTIONS.map(s => (
              <motion.div key={s} whileHover={{ x: 2 }} onClick={() => setSort(s)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer', fontSize: 13, color: sort === s ? 'var(--primary)' : 'var(--ink-2)', fontWeight: sort === s ? 700 : 500 }}>
                <motion.span animate={{ scale: sort === s ? 1 : 0.85 }} style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${sort === s ? 'var(--primary)' : 'var(--line)'}`, background: sort === s ? 'var(--primary)' : 'transparent', flexShrink: 0, position: 'relative' }}>
                  {sort === s && <span style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: '#FFF' }} />}
                </motion.span>
                {s}
              </motion.div>
            ))}
          </FilterGroup>

          <FilterGroup label="Price" hindi="मूल्य">
            <input type="range" min={499} max={10000} step={100} value={maxPrice}
              onChange={e => setMaxPrice(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--primary)', marginBottom: 10 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)' }}>
              <span>₹499</span>
              <span style={{ fontWeight: 700, color: 'var(--ink)' }}>Up to ₹{maxPrice.toLocaleString('en-IN')}</span>
            </div>
          </FilterGroup>

          <FilterGroup label="Size" hindi="नाप">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SIZES.map(s => {
                const sel = selectedSizes.includes(s);
                return (
                  <motion.button key={s} onClick={() => toggleArr(s, selectedSizes, setSelectedSizes)} whileTap={{ scale: 0.94 }} style={{
                    minWidth: 46, height: 40, borderRadius: 10,
                    border: sel ? '2px solid var(--ink)' : '1px solid var(--line)',
                    background: sel ? 'var(--ink)' : 'var(--surface)',
                    color: sel ? '#FFF' : 'var(--ink)',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}>{s}</motion.button>
                );
              })}
            </div>
          </FilterGroup>

          <FilterGroup label="Colour" hindi="रंग">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {COLORS.map(c => {
                const sel = selectedColors.includes(c.name);
                return (
                  <motion.button key={c.name} title={c.name} onClick={() => toggleArr(c.name, selectedColors, setSelectedColors)} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.9 }}
                    style={{ width: 30, height: 30, borderRadius: '50%', background: c.hex, border: '2px solid #FFF', outline: sel ? '2px solid var(--ink)' : '1px solid rgba(0,0,0,0.08)', outlineOffset: 2, cursor: 'pointer' }} />
                );
              })}
            </div>
          </FilterGroup>

          <FilterGroup label="Fabric" hindi="कपड़ा">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {FABRICS.map(f => {
                const sel = selectedFabrics.includes(f);
                return (
                  <motion.button key={f} onClick={() => toggleArr(f, selectedFabrics, setSelectedFabrics)} whileTap={{ scale: 0.96 }} className={`chip ${sel ? 'chip-active' : ''}`} style={{ cursor: 'pointer', fontSize: 11 }}>{f}</motion.button>
                );
              })}
            </div>
          </FilterGroup>
        </aside>

        {/* ─── Mobile filter drawer ─── */}
        <AnimatePresence>
          {filterOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setFilterOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(26,16,14,0.5)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
              <motion.div onClick={e => e.stopPropagation()} initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                style={{ width: '90%', maxWidth: 360, height: '100vh', background: 'var(--bg)', padding: 24, overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h3 className="serif" style={{ fontSize: 22 }}>Filters</h3>
                  <button onClick={() => setFilterOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><FiX size={20} /></button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                  <MobileFilters {...{ sort, setSort, maxPrice, setMaxPrice, selectedSizes, setSelectedSizes, selectedColors, setSelectedColors, selectedFabrics, setSelectedFabrics, toggleArr }} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Grid ─── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {isLoading ? (
            <div className="cp-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="ve-skeleton" style={{ aspectRatio: '3/4', borderRadius: 16 }} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div className="serif" style={{ fontSize: 28, marginBottom: 8, color: 'var(--ink)' }}>No pieces match these filters</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Try widening your selection or clearing filters</div>
            </div>
          ) : (
            <>
              <motion.div variants={stagger} initial="hidden" animate="show" className="cp-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
                {products.map(p => (
                  <motion.div key={p._id} variants={fadeUp} whileHover={{ y: -6, transition: { duration: 0.25 } }}>
                    <ProductCard product={p} />
                  </motion.div>
                ))}
              </motion.div>

              {pages > 1 && (
                <Reveal>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 48 }}>
                    {[...Array(pages)].map((_, i) => (
                      <motion.button key={i} onClick={() => setPage(i + 1)} whileTap={{ scale: 0.94 }} className={`chip ${page === i + 1 ? 'chip-active' : ''}`} style={{ cursor: 'pointer', minWidth: 36, justifyContent: 'center' }}>
                        {i + 1}
                      </motion.button>
                    ))}
                  </div>
                </Reveal>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .cp-layout { flex-direction: column !important; }
          .cp-sidebar { display: none !important; }
          .cp-filter-toggle { display: inline-flex !important; }
          .cp-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}

function FilterGroup({ label, hindi, children }) {
  return (
    <Reveal>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink)' }}>{label}</span>
          {hindi && <span className="hindi" style={{ fontSize: 11, color: 'var(--muted)' }}>{hindi}</span>}
        </div>
        {children}
      </div>
    </Reveal>
  );
}

function MobileFilters({ sort, setSort, maxPrice, setMaxPrice, selectedSizes, setSelectedSizes, selectedColors, setSelectedColors, selectedFabrics, setSelectedFabrics, toggleArr }) {
  return (
    <>
      <FilterGroup label="Sort by">
        {SORT_OPTIONS.map(s => (
          <div key={s} onClick={() => setSort(s)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', cursor: 'pointer', fontSize: 13, color: sort === s ? 'var(--primary)' : 'var(--ink-2)', fontWeight: sort === s ? 700 : 500 }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${sort === s ? 'var(--primary)' : 'var(--line)'}`, background: sort === s ? 'var(--primary)' : 'transparent', flexShrink: 0 }} />
            {s}
          </div>
        ))}
      </FilterGroup>
      <FilterGroup label="Price" hindi="मूल्य">
        <input type="range" min={499} max={10000} step={100} value={maxPrice} onChange={e => setMaxPrice(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--primary)' }} />
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>Up to ₹{maxPrice.toLocaleString('en-IN')}</div>
      </FilterGroup>
      <FilterGroup label="Size">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {SIZES.map(s => (
            <button key={s} onClick={() => toggleArr(s, selectedSizes, setSelectedSizes)} style={{ minWidth: 46, height: 40, borderRadius: 10, border: selectedSizes.includes(s) ? '2px solid var(--ink)' : '1px solid var(--line)', background: selectedSizes.includes(s) ? 'var(--ink)' : 'var(--surface)', color: selectedSizes.includes(s) ? '#FFF' : 'var(--ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{s}</button>
          ))}
        </div>
      </FilterGroup>
      <FilterGroup label="Colour">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {COLORS.map(c => (
            <button key={c.name} title={c.name} onClick={() => toggleArr(c.name, selectedColors, setSelectedColors)} style={{ width: 30, height: 30, borderRadius: '50%', background: c.hex, border: '2px solid #FFF', outline: selectedColors.includes(c.name) ? '2px solid var(--ink)' : '1px solid rgba(0,0,0,0.08)', outlineOffset: 2, cursor: 'pointer' }} />
          ))}
        </div>
      </FilterGroup>
    </>
  );
}
